import { ReactElement } from 'react';
import { Table, Input, Popconfirm, Space } from 'antd';
import { EditOutlined, DeleteOutlined, SearchOutlined, EyeOutlined } from '@ant-design/icons';
import { paginationSizeOptions } from './constants';
import { Button } from 'app/atoms';
import { FilterDropdownProps } from 'antd/es/table/interface';
import RangeFilterDropdown from './filter-dropdowns/RangeFilterDropdown';
import CheckboxFilterDropdown from './filter-dropdowns/ChecboxFilterDropdown';
import RadioFilterDropdown from './filter-dropdowns/RadioFilterDropdown';
import DateRangeFilterDropdown from './filter-dropdowns/DateRangeFilterDropdown';
import { EnhancedTableProps } from './types';

const { Search } = Input;

type FilterIndex = string | number | undefined;

type FilterWrapperProps = FilterDropdownProps & {
  onFilter: (filterIndex: FilterIndex, value: any) => void;
  filterIndex: FilterIndex;
  filterOptions: any;
};

const SearchWrapper = ({ confirm, onFilter, filterIndex, filterOptions }: FilterWrapperProps) => (
  <Search
    className="p-2"
    allowClear
    enterButton
    defaultValue={filterOptions.defaultValue}
    onSearch={(value) => {
      confirm({ closeDropdown: true });
      onFilter(filterIndex, value);
    }}
    placeholder="type to search ..."
  />
);

const RangeWrapper = ({ confirm, onFilter, filterIndex, filterOptions }: FilterWrapperProps) => (
  <RangeFilterDropdown
    from={filterOptions.from}
    to={filterOptions.to}
    defaultValue={filterOptions.defaultValue}
    onApplyFilter={(value) => {
      confirm({ closeDropdown: true });
      onFilter(filterIndex, value);
    }}
  />
);

const CheckboxWrapper = ({ confirm, onFilter, filterIndex, filterOptions }: FilterWrapperProps) => (
  <CheckboxFilterDropdown
    options={filterOptions.options}
    defaultValue={filterOptions.defaultValue}
    onApplyFilter={(value) => {
      confirm({ closeDropdown: true });
      onFilter(filterIndex, value);
    }}
  />
);

const RadioWrapper = ({ confirm, onFilter, filterIndex, filterOptions }: FilterWrapperProps) => (
  <RadioFilterDropdown
    options={filterOptions.options}
    defaultValue={filterOptions.defaultValue}
    onApplyFilter={(value) => {
      confirm({ closeDropdown: true });
      onFilter(filterIndex, value);
    }}
  />
);

const DateRangeWrapper = ({ confirm, onFilter, filterIndex, filterOptions }: FilterWrapperProps) => (
  <DateRangeFilterDropdown
    onApplyFilter={(value) => {
      confirm({ closeDropdown: true });
      onFilter(filterIndex, value);
    }}
    defaultValue={filterOptions.defaultValue}
  />
);

function wrap(Wrapper: typeof SearchWrapper, onFilter: any, filterIndex: FilterIndex, filterOptions: any) {
  const Wrapped = (dropdownProps: FilterDropdownProps) => (
    <Wrapper {...dropdownProps} onFilter={onFilter} filterIndex={filterIndex} filterOptions={filterOptions} />
  );
  return Wrapped;
}

const ShowTotal = ({ total, range }: { total: number; range: [number, number] }) => (
  <div className="max-sm:hidden ml-2">{`${range[0]}-${range[1]} of ${total} records`}</div>
);

const EnhancedTable: <T>(props: EnhancedTableProps<T>) => ReactElement = (props) => {
  const { columns = [], onFilter, onEdit, onView, onRemove, showEdit, showView, showRemove, pageInfo, sortInfo, ...otherProps } = props;

  const getFilterDropdown = (column: (typeof columns)[number]) => {
    const { filterOptions, dataIndex } = column;
    if (!filterOptions || !dataIndex) return undefined;
    const resolvedFilterIndex = (filterOptions.filterIndex ?? dataIndex) as FilterIndex;

    switch (filterOptions.type) {
      case 'search':
        return wrap(SearchWrapper, onFilter, resolvedFilterIndex, filterOptions);
      case 'range':
        return wrap(RangeWrapper, onFilter, resolvedFilterIndex, filterOptions);
      case 'checkbox':
        return wrap(CheckboxWrapper, onFilter, resolvedFilterIndex, filterOptions);
      case 'radio':
        return wrap(RadioWrapper, onFilter, resolvedFilterIndex, filterOptions);
      case 'date-range':
        return wrap(DateRangeWrapper, onFilter, resolvedFilterIndex, filterOptions);
      default:
        return undefined;
    }
  };

  const getFilterIcon = (column: any) => {
    if (!column.filterOptions) return undefined;
    const { Icon } = column.filterOptions;
    const isActive = Boolean(column.filterOptions.defaultValue?.length || column.filterOptions.defaultValue);
    return Icon ? <Icon className={isActive ? 'text-[#000032]' : undefined} /> : <SearchOutlined className={isActive ? 'text-[#000032]' : undefined} />;
  };

  let enhancedColumns = columns.map((column) => {
    const enhancedColumn: typeof column = {
      filterIcon: getFilterIcon(column),
      filterDropdown: getFilterDropdown(column),
      ...column,
    };
    if (sortInfo && column.dataIndex === sortInfo.sortField) {
      enhancedColumn.sortOrder = sortInfo.sortOrder;
    }
    return enhancedColumn;
  });

  const hasRowActions = Boolean(showView || showEdit || showRemove);

  const renderActionsColumn = (record: any) => (
    <Space>
      {showView && <Button onClick={(e) => onView?.(e, record)} icon={<EyeOutlined />} aria-label="View" />}
      {showEdit && <Button onClick={(e) => onEdit?.(e, record)} icon={<EditOutlined />} aria-label="Edit" />}
      {showRemove && (
        <Popconfirm
          title="Delete the record"
          description="Are you sure you want to delete this record?"
          onConfirm={(e) => onRemove?.(e, record)}
          onCancel={(e) => e?.stopPropagation()}
          okText="Yes"
          cancelText="No"
        >
          <Button danger icon={<DeleteOutlined />} onClick={(e) => e.stopPropagation()} aria-label="Delete" />
        </Popconfirm>
      )}
    </Space>
  );

  if (hasRowActions) {
    enhancedColumns = [
      ...enhancedColumns,
      {
        title: 'Actions',
        key: 'actions',
        align: 'right',
        render: renderActionsColumn,
      },
    ];
  }

  return (
    <Table
      scroll={{ x: true }}
      columns={enhancedColumns}
      pagination={
        pageInfo
          ? {
              pageSize: Number(pageInfo.size),
              current: Number(pageInfo.page + 1),
              total: pageInfo.totalItems,
              pageSizeOptions: paginationSizeOptions,
              showSizeChanger: false,
              showLessItems: true,
              showTotal: (total, range) => <ShowTotal total={total} range={range} />,
            }
          : {
              pageSizeOptions: paginationSizeOptions,
              showSizeChanger: true,
              showTotal: (total, range) => <ShowTotal total={total} range={range} />,
            }
      }
      {...otherProps}
    />
  );
};

export default EnhancedTable;
